import { User } from "oidc-client-ts";

import { config } from "./config";

/**
 * Reads the current user from the same storage oidc-client-ts writes to.
 *
 * The alternative is threading the token through every call site from the auth
 * context. This keeps the query functions plain async functions, which makes
 * them far easier to use with React Query.
 */
function currentUser(): User | null {
  const raw = sessionStorage.getItem(
    `oidc.user:${config.authority}:${config.clientId}`,
  );
  return raw ? User.fromStorageString(raw) : null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const user = currentUser();
  if (!user?.access_token) {
    throw new ApiError(401, "Not signed in");
  }

  const res = await fetch(`${config.apiUrl}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${user.access_token}`,
      ...init.headers,
    },
  });

  if (res.status === 401) {
    // The token expired or was revoked. Reloading restarts the silent-renew /
    // login flow rather than leaving the UI stuck on a dead session.
    throw new ApiError(401, "Session expired — please sign in again.");
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new ApiError(res.status, detail || `Request failed (${res.status})`);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const api = {
  list: <T>(resource: string, status?: string) =>
    request<{ items: T[] }>(
      `/admin/${resource}${status ? `?status=${encodeURIComponent(status)}` : ""}`,
    ).then((r) => r.items),

  create: <T>(resource: string, body: unknown) =>
    request<T>(`/admin/${resource}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  update: <T>(resource: string, id: string, body: unknown) =>
    request<T>(`/admin/${resource}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  get: <T>(resource: string, id: string) =>
    request<T>(`/admin/${resource}/${id}`),

  put: <T>(resource: string, body: unknown) =>
    request<T>(`/admin/${resource}`, { method: "PUT", body: JSON.stringify(body) }),

  remove: (resource: string, id: string) =>
    request<void>(`/admin/${resource}/${id}`, { method: "DELETE" }),

  summary: () =>
    request<{
      inquiries: { total: number; new: number };
      leads: { total: number; new: number; demandNew: number; supplyNew: number };
      contracts: { total: number; active: number };
      cleaners: { total: number };
    }>("/admin/summary"),
};
